import type { ReactNode } from "react"

import type { SolarProposalDesignDto } from "~/api/dto/solar-proposal-design.dto"

import { SectionRail, SolarHeader } from "./sections/chrome"
import { Hero } from "./sections/hero"
import { PowurCare, SolarHomeownerApp, SolarNextSteps } from "./sections/story"
import {
  sectionData,
  type SolarPresentationData,
} from "./solar-presentation-data"
import { toSolarHeadline, toSolarHeroSavings } from "./solar-proposal-headline"
import {
  computeBaseYears,
  shownComputeBase,
  toShownComputePayout,
} from "./solar-proposal-savings"
import { solarSectionLinks } from "./solar-section-links"
import type { SolarStaleFigures } from "./solar-updates"
import { SolarAiComputeSection } from "./SolarAiComputeSection"
import { SolarDesignSection } from "./SolarDesignSection"
import { SolarEquipmentSection } from "./SolarEquipmentSection"
import { SolarImpactSection } from "./SolarImpactSection"
import { SolarPricingSection } from "./SolarPricingSection"
import { SolarSavingsSection } from "./SolarSavingsSection"
import type { SolarUpdatesRailState } from "./use-solar-updates"

/**
 * Per-section seller controls. Each renders inside its section only when the
 * page passes it, so a page without them shows the document and nothing that
 * edits it. Fixes for out-of-date figures live in the seller's Updates panel,
 * never in the document.
 */
export type SolarSectionActions = {
  /** Edit design, or Edit in Aurora, beside the aerial card. */
  readonly design?: (design: SolarProposalDesignDto) => ReactNode
  /** Change in Setup, beside the AI compute section's heading. */
  readonly compute?: ReactNode
  /** Get LightReach pricing, beside a LightReach plan's missing terms. */
  readonly lightReachPricing?: ReactNode
  /** Reserve now, beneath the investment card, before a contract exists. */
  readonly reserve?: ReactNode
}

/**
 * The Solar Proposal V2 document, rendered from one data prop: header and
 * hero, design, equipment, savings, investment, Powur Care, what happens
 * next, impact and the homeowner app. It reads nothing itself — no query, no
 * session — so the seller page, a public link and the print route render the
 * same document from their own reads.
 *
 * - `chrome`: the seller's Setup entry, drawers and rail, after the document.
 * - `heroStatus`: beside the hero's status (the seller's Contract status link).
 * - `sectionActions`: per-section seller controls (Edit design).
 * - `staleFigures`: seller page only, a dotted underline on figures that are
 *   out of date.
 * - `rail`: seller page only, the Updates panel's hold on the section rail.
 * - `investment`: replaces the savings and investment sections (the money),
 *   so a public page can withhold them behind "Pricing is being updated".
 * - `savingsYear`: the year the savings dial opens on (print).
 */
export function SolarProposalPresentation({
  data,
  chrome,
  heroStatus,
  sectionActions,
  investment,
  savingsYear,
  staleFigures,
  rail,
}: {
  data: SolarPresentationData
  chrome?: ReactNode
  heroStatus?: ReactNode
  sectionActions?: SolarSectionActions
  investment?: ReactNode
  savingsYear?: number
  staleFigures?: SolarStaleFigures
  rail?: SolarUpdatesRailState
}): JSX.Element {
  const { header } = data
  const design = sectionData(data.design)
  const equipment = sectionData(data.equipment)
  const savings = sectionData(data.savings)
  const pricing = sectionData(data.pricingSummary)
  const impact = sectionData(data.impact)
  const { aiCompute } = data
  const headline = toSolarHeadline(header)
  // Withheld money withholds the hero's savings statement with it: the hero
  // states the savings section's figure, never one the page isn't showing.
  // The base the compute section shows; the hero and savings count the node
  // only when theirs is the same, so the page never shows two bases.
  const ledgerMonthlyBase =
    toShownComputePayout(aiCompute?.payout)?.monthlyBase ?? null
  const heroSavings =
    investment == null && savings
      ? toSolarHeroSavings(header, savings, ledgerMonthlyBase)
      : null
  // The savings years whose figures count the compute base; null when no
  // savings figure on the page does, so the disclosure claims none.
  const computeBase =
    investment == null && savings
      ? shownComputeBase(savings.savings, savings.net, ledgerMonthlyBase)
      : undefined
  const computeSection = aiCompute ? (
    <SolarAiComputeSection
      aiCompute={aiCompute}
      billShare={
        investment == null ? (savings?.aiComputeBillShare ?? null) : null
      }
      savingsComputeYears={computeBase ? computeBaseYears(computeBase) : null}
      action={sectionActions?.compute}
    />
  ) : null

  return (
    <>
      <SolarHeader headline={headline} />
      {/* A read answering swaps its notice for the real section under the same
          anchor, so the rail re-observes to follow the new node. It keeps its
          state rather than remounting, so a pin the reader released stays
          released. */}
      <SectionRail
        hidden={rail?.hidden}
        staleSections={rail?.staleSections}
        observeKey={`${Boolean(design)}:${Boolean(equipment)}:${Boolean(savings)}:${Boolean(pricing)}:${Boolean(impact)}:${Boolean(aiCompute)}`}
        sections={solarSectionLinks({
          design,
          equipment,
          savings: investment == null ? savings : null,
          powurCare: header.powurCareSelected,
          impact,
          aiCompute: aiCompute != null,
        })}
      />
      <Hero
        headline={headline}
        savings={heroSavings}
        savingsPending={investment == null && data.savings.status === `loading`}
        statusAction={heroStatus}
        stale={staleFigures?.savings}
      />
      <SolarDesignSection
        header={header}
        state={data.design}
        action={sectionActions?.design}
      />
      <SolarEquipmentSection
        proposalId={header.id}
        state={data.equipment}
        aiCompute={aiCompute}
      />
      {investment != null ? (
        <>
          {investment}
          {computeSection}
        </>
      ) : (
        <>
          <SolarSavingsSection
            proposalId={header.id}
            state={data.savings}
            stale={staleFigures?.savings}
            initialYear={savingsYear}
            computeMonthlyBase={ledgerMonthlyBase}
          />
          {computeSection}
          <SolarPricingSection
            state={data.pricingSummary}
            stale={staleFigures?.pricing}
            lightReachPricing={sectionActions?.lightReachPricing}
            action={sectionActions?.reserve}
          />
        </>
      )}
      {header.powurCareSelected ? <PowurCare /> : null}
      <SolarNextSteps />
      <SolarImpactSection state={data.impact} />
      <SolarHomeownerApp />
      {chrome}
    </>
  )
}
